/**
 * Client wire contracts — the registry.
 *
 * See ./README.md for why these exist and why they are non-strict.
 *
 * The registry keys are `"<METHOD> <path>"` — the route the phone calls
 * outside the SDK. They stand for as long as those routes exist.
 */
import type { ZodType } from 'zod';

import { clientWireCopilotUploadResponseSchema } from './copilot-upload.contract';
import { clientWireFeedbackResponseSchema } from './feedback.contract';
import { clientWireHealthBrainSchema } from './health-brain.contract';

export * from './copilot-upload.contract';
export * from './feedback.contract';
export * from './health-brain.contract';

export interface ClientWireContract {
  /** Why the client cannot survive this shape changing. */
  readonly why: string;
  readonly schema: ZodType;
}

/**
 * Keyed `"<METHOD> <path>"`.
 *
 * The four CoPilot upload routes share one shape and each gets its own entry
 * rather than a wildcard: a wildcard here would keep passing if one of them
 * were removed.
 */
export const CLIENT_WIRE_CONTRACTS: Readonly<Record<string, ClientWireContract>> = Object.freeze({
  'POST /api/copilot/uploads/image': {
    why: 'Parsed with a bare `as` cast outside the SDK (expo-file-system UploadTask); a renamed field lands in the CoPilot turn as undefined.',
    schema: clientWireCopilotUploadResponseSchema,
  },
  'POST /api/copilot/uploads/document': {
    why: 'Same uploader and shape as the image route.',
    schema: clientWireCopilotUploadResponseSchema,
  },
  'POST /api/copilot/uploads/audio': {
    why: 'Same uploader; carries `transcript` rather than `extractedText`.',
    schema: clientWireCopilotUploadResponseSchema,
  },
  'POST /api/copilot/uploads/video': {
    why: 'Same uploader; carries `transcript` rather than `extractedText`.',
    schema: clientWireCopilotUploadResponseSchema,
  },
  'POST /api/feedback': {
    why: 'Parsed with a bare `as` cast over raw XHR multipart. Survives absence, not a retype — `"false"` is truthy.',
    schema: clientWireFeedbackResponseSchema,
  },
  'GET /api/health/brain': {
    why: 'Hand-rolled fetch in AboutScreen reading a NESTED `version`. Flattening it already produced "[object Object]+undefined" on a real screen.',
    schema: clientWireHealthBrainSchema,
  },
});

export type ClientWireContractKey = keyof typeof CLIENT_WIRE_CONTRACTS;
